package top.wcpe.allincore.ui.runtime.theme

import kotlin.jvm.JvmStatic
import kotlin.math.floor
import kotlin.math.roundToInt

/**
 * 运行时颜色函数：与编译期 `ColorMath.kt` 的 HSV 算法逐位对齐，供 DSL 在 prop 表达式中调用
 * `lighten/darken/alpha/mix`（如 `background = lighten(theme.primary, 0.1)`）。
 *
 * 输入为 ARGB int（由 `ValueCoercion.toIntColor` 解析自 #AARRGGBB 字符串或数字），输出统一为
 * #AARRGGBB 字符串，使下游 `ValueCoercion.toIntColor` 零改动消费。
 *
 * 纯函数、无状态。与编译期 ColorMath 保持同一算法以保证「同一表达式在 theme 块与 prop 中求值一致」。
 *
 * 整类的 255 / 360 / 60 等均为颜色与 HSV 算法的固有数值，与 ColorMath.kt 逐位对齐，故整体豁免
 * 魔法值检查（抽常量反而会割裂与编译期算法的逐位对照）。
 */
@Suppress("MagicNumber")
object ColorFunctions {

    /** 在 HSV 明度上提升 amt（0..1），alpha 不变。 */
    @JvmStatic
    fun lighten(argb: Int, amt: Double): String = adjustValue(argb, amt)

    /** 在 HSV 明度上降低 amt（0..1），alpha 不变。 */
    @JvmStatic
    fun darken(argb: Int, amt: Double): String = adjustValue(argb, -amt)

    /** 用给定 alpha（0..1）覆盖 alpha 通道，RGB 不变。 */
    @JvmStatic
    fun alpha(argb: Int, a: Double): String {
        val aa = (a.coerceIn(0.0, 1.0) * 255).roundToInt()
        return toHex(aa, (argb shr 16) and 0xFF, (argb shr 8) and 0xFF, argb and 0xFF)
    }

    /** 把 fg 以 alpha（0..1）叠加到 bg 上，返回不透明结果（对齐 ColorMath.alphaBlend）。 */
    @JvmStatic
    fun mix(bgArgb: Int, fgArgb: Int, t: Double): String {
        val a = t.coerceIn(0.0, 1.0)
        val r = mixCh((bgArgb shr 16) and 0xFF, (fgArgb shr 16) and 0xFF, a)
        val g = mixCh((bgArgb shr 8) and 0xFF, (fgArgb shr 8) and 0xFF, a)
        val b = mixCh(bgArgb and 0xFF, fgArgb and 0xFF, a)
        return toHex(255, r, g, b)
    }

    /** 逐通道（含 alpha）在 ARGB 之间插值，t∈[0,1]。供主题切换过渡逐帧合成。 */
    @JvmStatic
    fun lerpArgb(from: Int, to: Int, t: Double): Int {
        val a = lerpCh((from ushr 24) and 0xFF, (to ushr 24) and 0xFF, t)
        val r = lerpCh((from ushr 16) and 0xFF, (to ushr 16) and 0xFF, t)
        val g = lerpCh((from ushr 8) and 0xFF, (to ushr 8) and 0xFF, t)
        val b = lerpCh(from and 0xFF, to and 0xFF, t)
        return (a shl 24) or (r shl 16) or (g shl 8) or b
    }

    private fun mixCh(bv: Int, fv: Int, a: Double): Int = (fv * a + bv * (1 - a)).roundToInt().coerceIn(0, 255)

    private fun lerpCh(from: Int, to: Int, t: Double): Int = (from + (to - from) * t).roundToInt().coerceIn(0, 255)

    private fun adjustValue(argb: Int, delta: Double): String {
        val alpha = (argb ushr 24) and 0xFF
        val r = (argb shr 16) and 0xFF
        val g = (argb shr 8) and 0xFF
        val b = argb and 0xFF
        val hsv = rgbToHsv(r, g, b)
        val rgb = hsvToRgb(hsv[0], hsv[1], (hsv[2] + delta).coerceIn(0.0, 1.0))
        return toHex(alpha, rgb[0], rgb[1], rgb[2])
    }

    /** RGB(0..255) → HSV：h 为 0..360，s/v 为 0..1。与 ColorMath.rgbToHsv 对齐。 */
    private fun rgbToHsv(r: Int, g: Int, b: Int): DoubleArray {
        val rf = r / 255.0
        val gf = g / 255.0
        val bf = b / 255.0
        val mx = maxOf(rf, gf, bf)
        val mn = minOf(rf, gf, bf)
        val d = mx - mn
        val v = mx
        val s = if (mx == 0.0) 0.0 else d / mx
        val h = when {
            d == 0.0 -> 0.0
            mx == rf -> ((gf - bf) / d + (if (gf < bf) 6 else 0)) * 60
            mx == gf -> ((bf - rf) / d + 2) * 60
            else -> ((rf - gf) / d + 4) * 60
        }
        return doubleArrayOf(h, s, v)
    }

    /** HSV → RGB(0..255)。与 ColorMath.hsvToRgb 对齐。 */
    private fun hsvToRgb(h: Double, s: Double, v: Double): IntArray {
        val hh = ((h % 360) + 360) % 360 / 60.0
        val i = floor(hh).toInt()
        val f = hh - i
        val p = v * (1 - s)
        val q = v * (1 - f * s)
        val t = v * (1 - (1 - f) * s)
        val (rf, gf, bf) = when (i % 6) {
            0 -> Triple(v, t, p)
            1 -> Triple(q, v, p)
            2 -> Triple(p, v, t)
            3 -> Triple(p, q, v)
            4 -> Triple(t, p, v)
            else -> Triple(v, p, q)
        }
        return intArrayOf((rf * 255).roundToInt(), (gf * 255).roundToInt(), (bf * 255).roundToInt())
    }

    // KMP 迁移：common 无 String.format，手写两位大写十六进制（与原 %02X 逐字符等价）
    private fun toHex(a: Int, r: Int, g: Int, b: Int): String =
        "#" + hex2(a.coerceIn(0, 255)) + hex2(r.coerceIn(0, 255)) + hex2(g.coerceIn(0, 255)) + hex2(b.coerceIn(0, 255))

    /** 单通道 0..255 → 两位大写十六进制（等价 `%02X`）。 */
    private fun hex2(v: Int): String = v.toString(radix = 16).uppercase().padStart(2, '0')
}
